<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bx bx-check-square me-2"></i>
            Batch {{ action.charAt(0).toUpperCase() + action.slice(1) }} Claims ({{ claims.length }})
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>

        <div class="modal-body">
          <!-- Summary Alert -->
          <div class="alert" :class="action === 'approve' ? 'alert-success' : 'alert-warning'">
            <i :class="action === 'approve' ? 'bx bx-check-circle' : 'bx bx-info-circle'" class="me-2"></i>
            <strong>{{ action === 'approve' ? 'Approving' : 'Rejecting' }} {{ claims.length }} claims</strong>
            <div class="mt-2">
              Total Amount: <strong>ETB {{ formatAmount(totalAmount) }}</strong>
              <span v-if="action === 'approve'"> | Available Balance: <strong>ETB {{ formatAmount(totalAvailableBalance) }}</strong></span>
            </div>
          </div>

          <!-- Claims List -->
          <div class="card border mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0">Selected Claims</h6>
            </div>
            <div class="card-body">
              <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                <table class="table table-sm">
                  <thead>
                  <tr>
                    <th>Claim #</th>
                    <th>Member</th>
                    <th>Hospital</th>
                    <th class="text-end">Amount</th>
                    <th v-if="action === 'approve'" class="text-end">Approved Amount</th>
                    <th class="text-end">Balance</th>
                    <th>Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="claim in claims" :key="claim.claim_id"
                      :class="{'table-warning': !claim.member_active}">
                    <td>
                      <small>{{ claim.claim_number }}</small>
                    </td>
                    <td>
                      <div>
                        <small class="fw-bold">{{ claim.first_name }} {{ claim.surname }}</small>
                        <br>
                        <small class="text-muted">{{ claim.cbhi_number }}</small>
                        <span v-if="!claim.member_active" class="badge bg-danger ms-2">Inactive</span>
                      </div>
                    </td>
                    <td>
                      <small>{{ claim.hospital_name }}</small>
                    </td>
                    <td class="text-end">ETB {{ formatAmount(claim.total_amount) }}</td>
                    <td v-if="action === 'approve'" class="text-end">
                      <input
                          v-if="claim.member_active"
                          type="number"
                          class="form-control form-control-sm"
                          v-model.number="approvedAmounts[claim.claim_id]"
                          :max="Math.min(claim.total_amount, claim.remaining_balance || 0)"
                          min="0"
                          step="0.01"
                          style="width: 120px;"
                      >
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td class="text-end">ETB {{ formatAmount(claim.remaining_balance || 0) }}</td>
                    <td>
                      <span v-if="claim.member_active" class="badge bg-success">Ready</span>
                      <span v-else class="badge bg-danger">Inactive Member</span>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Processing Reason Section -->
          <div class="row">
            <div class="col-md-6">
              <div class="card border">
                <div class="card-header bg-light">
                  <h6 class="mb-0">{{ action === 'approve' ? 'Approval' : 'Rejection' }} Reason</h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <label class="form-label">Reason Type <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="selectedReasonCode" required>
                      <option value="">Choose a reason...</option>
                      <option v-for="reason in availableReasons" :key="reason.reason_code" :value="reason.reason_code">
                        {{ reason.reason_name }}
                      </option>
                    </select>
                  </div>

                  <div class="mb-3">
                    <label class="form-label">Additional Details</label>
                    <textarea
                        class="form-control"
                        rows="3"
                        v-model="reasonDetails"
                        :placeholder="`Provide additional ${action === 'approve' ? 'justification' : 'explanation'} for this batch ${action}...`"
                        :required="action === 'reject'"
                    ></textarea>
                  </div>

                  <!-- Processing Notes -->
                  <div class="mb-3">
                    <label class="form-label">Processing Notes</label>
                    <textarea
                        class="form-control"
                        rows="2"
                        v-model="processingNotes"
                        placeholder="Any additional notes about this batch processing..."
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <!-- Processing Summary -->
              <div class="card border">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Processing Summary</h6>
                </div>
                <div class="card-body">
                  <div class="row text-center mb-3">
                    <div class="col-4">
                      <h5 class="text-success">{{ eligibleClaims.length }}</h5>
                      <small class="text-muted">Eligible</small>
                    </div>
                    <div class="col-4">
                      <h5 class="text-warning">{{ ineligibleClaims.length }}</h5>
                      <small class="text-muted">Ineligible</small>
                    </div>
                    <div class="col-4">
                      <h5 class="text-info">{{ overBudgetCount }}</h5>
                      <small class="text-muted">Over Budget</small>
                    </div>
                  </div>

                  <hr>

                  <div v-if="action === 'approve'" class="mb-3">
                    <div class="d-flex justify-content-between">
                      <span>Total Approved Amount:</span>
                      <strong class="text-success">ETB {{ formatAmount(totalApprovedAmount) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span>Patient Contribution:</span>
                      <strong class="text-info">ETB {{ formatAmount(totalPatientContribution) }}</strong>
                    </div>
                  </div>

                  <!-- Legacy support for rejection reason type -->
                  <div v-if="action === 'reject'" class="mb-3">
                    <label class="form-label">Legacy Reason Type</label>
                    <select class="form-select" v-model="rejectionReasonType">
                      <option value="">Select a reason...</option>
                      <option value="invalid_diagnosis">Invalid/Incomplete Diagnosis</option>
                      <option value="service_not_covered">Service Not Covered</option>
                      <option value="exceeded_limit">Exceeded Coverage Limit</option>
                      <option value="missing_documents">Missing Required Documents</option>
                      <option value="duplicate_claim">Duplicate Claim</option>
                      <option value="batch_rejection">Batch Rejection</option>
                      <option value="other">Other</option>
                    </select>
                    <small class="text-muted">This is for backward compatibility</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Warnings and Info -->
          <div v-if="ineligibleClaims.length > 0" class="alert alert-warning mt-3">
            <i class="bx bx-error-circle me-2"></i>
            <strong>Warning:</strong> {{ ineligibleClaims.length }} claims have inactive members and will be skipped during processing.
          </div>

          <div v-if="overBudgetCount > 0 && action === 'approve'" class="alert alert-info mt-3">
            <i class="bx bx-info-circle me-2"></i>
            <strong>Note:</strong> {{ overBudgetCount }} claims exceed member available balance. These will be adjusted to maximum available balance.
          </div>

          <!-- Validation Errors -->
          <div v-if="validationErrors.length > 0" class="alert alert-danger mt-3">
            <i class="bx bx-error-circle me-2"></i>
            <strong>Please fix the following errors:</strong>
            <ul class="mb-0 mt-2">
              <li v-for="error in validationErrors" :key="error">{{ error }}</li>
            </ul>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button
              type="button"
              :class="action === 'approve' ? 'btn btn-success' : 'btn btn-danger'"
              @click="processBatch"
              :disabled="!canProcess || processing"
          >
            <span v-if="processing" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <i v-else :class="action === 'approve' ? 'bx bx-check' : 'bx bx-x'" class="me-1"></i>
            {{ processing ? 'Processing...' : (action === 'approve' ? 'Approve' : 'Reject') }}
            {{ eligibleClaims.length }} Claims
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BatchProcessModal",
  props: {
    claims: {
      type: Array,
      required: true
    },
    action: {
      type: String,
      required: true,
      validator: value => ['approve', 'reject'].includes(value)
    }
  },
  data() {
    return {
      // New enhanced reason fields
      selectedReasonCode: '',
      reasonDetails: '',
      processingNotes: '',
      approvedAmounts: {},
      processing: false,
      validationErrors: [],
      availableReasons: [],

      // Legacy fields for backward compatibility
      rejectionReasonType: '',
      rejectionReasonText: '' // This will be populated from reasonDetails
    };
  },
  computed: {
    totalAmount() {
      return this.claims.reduce((sum, claim) => sum + parseFloat(claim.total_amount || 0), 0);
    },

    totalAvailableBalance() {
      return this.claims.reduce((sum, claim) => sum + parseFloat(claim.remaining_balance || 0), 0);
    },

    eligibleClaims() {
      if (this.action === 'approve') {
        return this.claims.filter(claim => claim.member_active);
      }
      return this.claims; // All claims can be rejected
    },

    ineligibleClaims() {
      if (this.action === 'approve') {
        return this.claims.filter(claim => !claim.member_active);
      }
      return []; // No ineligible claims for rejection
    },

    overBudgetCount() {
      if (this.action !== 'approve') return 0;
      return this.eligibleClaims.filter(claim =>
          parseFloat(claim.total_amount || 0) > parseFloat(claim.remaining_balance || 0)
      ).length;
    },

    totalApprovedAmount() {
      if (this.action !== 'approve') return 0;
      return this.eligibleClaims.reduce((sum, claim) => {
        const approvedAmount = this.approvedAmounts[claim.claim_id] || parseFloat(claim.total_amount || 0);
        return sum + parseFloat(approvedAmount);
      }, 0);
    },

    totalPatientContribution() {
      if (this.action !== 'approve') return 0;
      return this.eligibleClaims.reduce((sum, claim) => {
        const approvedAmount = this.approvedAmounts[claim.claim_id] || parseFloat(claim.total_amount || 0);
        const patientContribution = parseFloat(claim.total_amount || 0) - parseFloat(approvedAmount);
        return sum + Math.max(0, patientContribution);
      }, 0);
    },

    // Legacy support
    inactiveMemberCount() {
      return this.ineligibleClaims.length;
    },

    validClaimsCount() {
      return this.eligibleClaims.length;
    },

    canProcess() {
      this.validateForm();
      return this.validationErrors.length === 0 && !this.processing && this.eligibleClaims.length > 0;
    }
  },

  watch: {
    // Sync reasonDetails with legacy rejectionReasonText for backward compatibility
    reasonDetails(newVal) {
      this.rejectionReasonText = newVal;
    }
  },

  async mounted() {
    this.initializeApprovedAmounts();
    await this.loadReasons();
  },

  methods: {
    initializeApprovedAmounts() {
      // Initialize approved amounts with safe maximum amounts
      this.claims.forEach(claim => {
        if (claim.member_active) {
          const claimedAmount = parseFloat(claim.total_amount || 0);
          const remainingBalance = parseFloat(claim.remaining_balance || 0);
          const maxAmount = Math.min(claimedAmount, remainingBalance);
          this.approvedAmounts[claim.claim_id] = Math.max(0, maxAmount);
        }
      });
    },

    async loadReasons() {
      try {
        // Load reasons based on action type
        if (this.action === 'approve') {
          this.availableReasons = [
            { reason_code: 'FULL_COVERAGE', reason_name: 'Full Coverage Approved' },
            { reason_code: 'PARTIAL_COVERAGE', reason_name: 'Partial Coverage Approved' },
            { reason_code: 'EMERGENCY_CARE', reason_name: 'Emergency Care Approved' },
            { reason_code: 'PREVENTIVE_CARE', reason_name: 'Preventive Care Approved' },
            { reason_code: 'CHRONIC_CONDITION', reason_name: 'Chronic Condition Management' },
            { reason_code: 'MATERNITY_CARE', reason_name: 'Maternity Care Approved' },
            { reason_code: 'PEDIATRIC_CARE', reason_name: 'Pediatric Care Approved' },
            { reason_code: 'EXCEPTIONAL_CASE', reason_name: 'Exceptional Case Approved' }
          ];
        } else {
          this.availableReasons = [
            { reason_code: 'INVALID_DIAGNOSIS', reason_name: 'Invalid/Incomplete Diagnosis' },
            { reason_code: 'SERVICE_NOT_COVERED', reason_name: 'Service Not Covered' },
            { reason_code: 'EXCEEDED_LIMIT', reason_name: 'Coverage Limit Exceeded' },
            { reason_code: 'MISSING_DOCUMENTS', reason_name: 'Missing Required Documents' },
            { reason_code: 'DUPLICATE_CLAIM', reason_name: 'Duplicate Claim' },
            { reason_code: 'MEMBER_INACTIVE', reason_name: 'Member Inactive' },
            { reason_code: 'EXPIRED_COVERAGE', reason_name: 'Coverage Expired' },
            { reason_code: 'INVALID_PROVIDER', reason_name: 'Invalid Healthcare Provider' },
            { reason_code: 'FRAUDULENT_CLAIM', reason_name: 'Fraudulent Activity Suspected' },
            { reason_code: 'INCOMPLETE_INFORMATION', reason_name: 'Incomplete Claim Information' }
          ];
        }
      } catch (error) {
        console.error('Failed to load reasons:', error);
      }
    },

    validateForm() {
      this.validationErrors = [];

      // Enhanced validation
      if (!this.selectedReasonCode) {
        this.validationErrors.push(`${this.action === 'approve' ? 'Approval' : 'Rejection'} reason is required`);
      }

      if (this.action === 'reject' && (!this.reasonDetails || this.reasonDetails.trim().length < 10)) {
        this.validationErrors.push('Detailed explanation is required for rejections (minimum 10 characters)');
      }

      if (this.action === 'approve') {
        // Validate approved amounts
        for (const claim of this.eligibleClaims) {
          const approvedAmount = this.approvedAmounts[claim.claim_id];
          if (!approvedAmount || approvedAmount <= 0) {
            this.validationErrors.push(`Invalid approved amount for claim ${claim.claim_number}`);
            break;
          }

          const maxAmount = Math.min(
              parseFloat(claim.total_amount || 0),
              parseFloat(claim.remaining_balance || 0)
          );
          if (approvedAmount > maxAmount) {
            this.validationErrors.push(`Approved amount exceeds available balance for claim ${claim.claim_number}`);
            break;
          }
        }
      }

      if (this.eligibleClaims.length === 0) {
        this.validationErrors.push('No eligible claims to process');
      }
    },

    async processBatch() {
      if (!this.canProcess) return;

      this.processing = true;

      try {
        const reasonName = this.availableReasons.find(r => r.reason_code === this.selectedReasonCode)?.reason_name || '';

        let fullReason;
        if (this.action === 'approve') {
          fullReason = `${this.selectedReasonCode}: ${reasonName}`;
          if (this.reasonDetails && this.reasonDetails.trim()) {
            fullReason += ` - ${this.reasonDetails.trim()}`;
          }
        } else {
          fullReason = `${this.selectedReasonCode}: ${reasonName} - ${this.reasonDetails}`;
        }

        // Enhanced reasons object
        const reasons = {
          [`${this.action}Reason`]: fullReason,
          processingNotes: this.processingNotes
        };

        if (this.action === 'approve') {
          reasons.approvedAmounts = this.approvedAmounts;
        }

        // For backward compatibility, also emit the old format
        if (this.action === 'reject') {
          // Try new format first, fall back to old format
          if (typeof this.$emit === 'function') {
            await this.$emit('process', this.action, reasons);
          } else {
            // Fallback for old implementation
            await this.$emit('process', this.action, fullReason);
          }
        } else {
          await this.$emit('process', this.action, reasons);
        }

      } catch (error) {
        console.error('Error processing batch:', error);
      } finally {
        this.processing = false;
      }
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    }
  }
};
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.table-warning {
  background-color: rgba(255, 193, 7, 0.1);
}

.alert ul {
  padding-left: 1.5rem;
}

.form-control-sm {
  font-size: 0.875rem;
}

.table-responsive {
  border: 1px solid #dee2e6;
}

/* Custom styling for better UX */
.card-header {
  background-color: #f8f9fa !important;
  border-bottom: 1px solid #dee2e6;
}

.badge {
  font-size: 0.75em;
}

.text-success {
  color: #198754 !important;
}

.text-warning {
  color: #fd7e14 !important;
}

.text-info {
  color: #0dcaf0 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.btn:disabled {
  opacity: 0.65;
  pointer-events: none;
}
</style>